import { StyleDeclaration, StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { THEME } from '../../components-v2/Theme/theme';
import { baseStyleSheet } from '../../styles/styles';
import { Checkbox } from '../Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components-v2/Popover';
import { DisclosureIcon } from '../svgs/icons/DisclosureIcon';
import { RefreshIcon } from '../svgs/icons/RefreshIcon';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { TextInput } from '../TextInput';
import { s } from './styles';

const { spacing } = THEME;

// Types
export interface MultipleSelectProps {
	value: string[];
	onChange: (value: string[]) => void;
	children: React.ReactNode;
	isBusy?: boolean;
}
export interface MultipleSelectContentProps {
	children: React.ReactNode;
	isSearchable?: boolean;
	searchFieldPlaceholder?: string;
	searchInput?: string;
	onSearchValueChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
	onSearchClear?: () => void;
}

interface MultipleSelectValueProps {
	values: string[];
	placeholder?: string;
	valueLabel?: string;
}

interface MultipleSelectItemProps {
	value: string;
	selected?: boolean;
	children: React.ReactNode;
	styles?: StyleDeclaration[];
}

// Components
export const _MultipleSelect: React.FC<MultipleSelectProps> = ({ children }) => {
	const [open, setOpen] = React.useState(false);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			{children}
		</Popover>
	);
};

export const MultipleSelectTrigger: React.FC<{ children: React.ReactNode; styles?: StyleDeclarationValue }> = ({
	children,
	styles,
}) => {
	const context = React.useContext(MultipleSelectContext);

	return (
		<PopoverTrigger className={css(s.trigger, styles)} onClick={() => context?.setIsOpen(!context?.isOpen)}>
			<div className={css(s.triggerContent)}>
				{children}
				<DisclosureIcon type='chevron' className={css(s.disclosureIcon, context?.isOpen && s.disclosureIconOpen)} />
			</div>
		</PopoverTrigger>
	);
};

export const MultipleSelectContent: React.FC<MultipleSelectContentProps> = ({
	children,
	isSearchable,
	searchFieldPlaceholder,
	searchInput,
	onSearchValueChange,
	onSearchClear,
}) => {
	return (
		<PopoverContent
			className={css(s.content)}
			align='start' // Left align with trigger
			sideOffset={4} // Small gap between trigger and content
		>
			{isSearchable ? (
				<TextInput
					className={css(s.searchBox)}
					inputClassName={css(s.searchBoxInput)}
					leftAccessory={<SearchIcon className={css(s.searchIcon)} />}
					onChange={onSearchValueChange}
					placeholder={searchFieldPlaceholder}
					type='text'
					value={searchInput}
					rightAccessory={
						searchInput ? (
							<button className={css(s.clearSearch)} onClick={onSearchClear}>
								Clear
							</button>
						) : null
					}
				/>
			) : null}
			<div className={css(s.itemsContainer)}>{children}</div>
		</PopoverContent>
	);
};

export const MultipleSelectValue: React.FC<MultipleSelectValueProps> = ({ values, placeholder, valueLabel }) => {
	const displayText =
		valueLabel ||
		(values.length === 0
			? placeholder
			: values.length === 1
				? values[0]
				: `${values[0]} and ${values.length - 1} others`);

	return <span className={css(s.value)}>{displayText}</span>;
};

export const MultipleSelectItem: React.FC<MultipleSelectItemProps> = ({
	value,
	selected,
	children,
	styles: customStyles,
}) => {
	const context = React.useContext(MultipleSelectContext);

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (context?.onChange && !context.isBusy) {
			const newValues = selected ? context.value.filter(v => v !== value) : [...context.value, value];
			context.onChange(newValues);
		}
	};

	return (
		<div className={css(s.item, customStyles)} onClick={handleClick}>
			<div className={css(s.iconWrapper)}>
				{context?.isBusy ? (
					<RefreshIcon height={12} width={12} className={css(baseStyleSheet.spinningIcon)} />
				) : (
					<Checkbox checked={selected} readOnly />
				)}
			</div>
			<span style={{ marginLeft: spacing.sm }}>{children}</span>
		</div>
	);
};

// Context
interface MultipleSelectContextType {
	value: string[];
	onChange: (value: string[]) => void;
	isOpen: boolean;
	isBusy?: boolean;
	setIsOpen: (isOpen: boolean) => void;
}

const MultipleSelectContext = React.createContext<MultipleSelectContextType | null>(null);

// Provider wrapper
const MultipleSelectProvider: React.FC<MultipleSelectProps> = ({ children, value, onChange, isBusy }) => {
	const [isOpen, setIsOpen] = React.useState(false);

	return (
		<MultipleSelectContext.Provider value={{ value, onChange, isOpen, setIsOpen, isBusy }}>
			{children}
		</MultipleSelectContext.Provider>
	);
};

// Wrap the main component with the provider
const WrappedMultipleSelect: React.FC<MultipleSelectProps> = props => {
	return (
		<MultipleSelectProvider {...props}>
			<_MultipleSelect {...props} />
		</MultipleSelectProvider>
	);
};

export { WrappedMultipleSelect as MultipleSelect };
